import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";

const EveryTypeOfCyberAttack: React.FC = () => {
  // State to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    "A": true,
    "B": true,
    "C": true,
    "D": true,
    "E": true,
    "F": true,
    "G": true,
    "H": true,
    "I": true
  });

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Organize attacks by category
  const attackCategories = {
    "A": {
      title: "Network-Based Attacks",
      attacks: [
        "ARP Poisoning/Spoofing",
        "DNS Cache Poisoning/Spoofing",
        "DNS Tunneling",
        "Denial-of-Service (DoS)",
        "Distributed Denial-of-Service (DDoS)",
        "DNS Amplification",
        "Drive-by Download",
        "Eavesdropping/Sniffing",
        "Evil Twin (Rogue Wi-Fi)",
        "IP Spoofing",
        "Man-in-the-Middle (MitM)",
        "Packet Injection",
        "Ping Flood",
        "Replay Attack",
        "SSL Stripping",
        "SYN Flood",
        "VLAN Hopping",
        "Wardriving",
        "5G Signaling Storm",
        "QUIC Protocol Abuse",
        "Network Boundary Bridging",
        "ICMP Tunneling",
        "NTP Amplification",
        "SD-WAN Misconfiguration Exploitation",
        "Satellite Link Hijacking",
        "Zero-Trust Network Bypass",
        "TCP Middlebox Reflection",
        "SCADA/ICS Protocol Fuzzing",
        "BGP Route Leak/Hijacking",
        "Geneva (Genetic Evasion)",
        "Time Synchronization Attack (PTP/NTP)",
      ]
    },
    "B": {
      title: "Web & Application Attacks",
      attacks: [
        "Buffer Overflow",
        "Clickjacking (UI Redress)",
        "Command Injection",
        "Cross-Site Request Forgery (CSRF/XSRF)",
        "Cross-Site Scripting (XSS)",
        "Directory Traversal/Path Traversal",
        "File Inclusion (LFI/RFI)",
        "Formjacking",
        "HTTP Flood",
        "Insecure Direct Object Reference (IDOR)",
        "LDAP Injection",
        "Remote Code Execution (RCE)",
        "Server-Side Request Forgery (SSRF)",
        "SQL Injection (SQLi)",
        "XML External Entity (XXE) Injection",
        "XPath Injection",
        "Template Injection",
        "Broken Access Control Exploitation",
        "Security Misconfiguration Exploitation",
        "Software Supply Chain Compromise",
        "JavaScript Injection at Scale",
        "API Abuse/Overloading",
        "HTTP/2 Rapid Reset",
        "WebSocket Hijacking",
        "gRPC Abuse",
        "Serverless Function RCE",
        "Mass Assignment Exploitation",
        "JWT Token Misconfiguration",
        "GraphQL Batching & Nested Query Attacks",
        "OAuth 2.0 Device Flow Phishing",
        "Cloud Metadata Service Exploitation (IMDSv1)",
        "DOM Clobbering",
        "Web Cache Poisoning via Unkeyed Inputs",
        "Prototype Pollution (JavaScript)",
        "Deserialization Attacks (Insecure Deserialization)",
      ]
    },
    "C": {
      title: "Social Engineering & Deception",
      attacks: [
        "Baiting",
        "Brand Impersonation",
        "Business Email Compromise (BEC)",
        "Catfishing",
        "Domain Spoofing",
        "Email Spoofing",
        "Honey Trap",
        "Phishing",
        "Pretexting",
        "Quid Pro Quo",
        "Scareware",
        "Smishing (SMS Phishing)",
        "Social Engineering",
        "Spear Phishing",
        "Tailgating/Piggybacking",
        "Typosquatting/URL Hijacking",
        "Vishing (Voice Phishing)",
        "Watering Hole Attack",
        "Whaling",
        "Deepfake Vishing",
        "AI-Generated Deepfake Video Phishing",
        "Agentic AI Social Engineering",
        "Narrative Attacks/Disinformation Campaigns",
        "Adversarial Prompting for AI Assistants",
        "Romance Scam Evolution",
        "QR Code Phishing (Quishing)",
        "Voiceprint Cloning for Real-Time BEC",
        "AI-Powered Reconnaissance for Personalization",
        "SIM Jacking (SIM Swap) as an Enabler",
        "Hybrid Vishing+Smishing",
        "Credential Harvesting via Fake Browser Updates",
        "Evil Twin Wi-Fi + Captive Portal Phishing",
      ]
    },
    "D": {
      title: "Malware & Malicious Software",
      attacks: [
        "Adware",
        "Backdoor",
        "Bootkit",
        "Botnet",
        "Cryptojacking",
        "Fileless Malware",
        "Keylogger",
        "Logic Bomb",
        "Polymorphic Malware",
        "Ransomware",
        "Rootkit",
        "Spyware",
        "Trojan Horse",
        "Virus",
        "Worm",
        "Zombie (Bot)",
        "Wiper Malware",
        "AI-Enhanced Polymorphic Malware",
        "Ransomware with Double/Triple Extortion",
        "Modular Malware Frameworks",
        "Containerized Malware",
        "Firmware Rootkits",
        "Bootkit Evolution (BlackLotus/UEFI)",
        "Living-Off-The-Land Binaries (LOLBins) Malware",
        "Reflective DLL Injection",
        "Process Hollowing",
        "DRAM-based Malware (Rowhammer-assisted)",
        "Malicious Browser Extensions",
        "IoT Botnets for Large-Scale DDoS",
      ]
    },
    "E": {
      title: "Credential & Authentication Attacks",
      attacks: [
        "Brute Force Attack",
        "Credential Stuffing",
        "Dictionary Attack",
        "Golden Ticket Attack",
        "Kerberoasting",
        "Pass-the-Hash",
        "Pass-the-Ticket",
        "Password Spraying",
        "Rainbow Table Attack",
        "Session Hijacking/Fixation",
        "SIM Swapping (SIM Jacking)",
        "AS-REP Roasting",
        "Non-Human Identity (NHI) Abuse",
        "Cloud Identity Exploitation",
        "Skeleton Key Attacks",
        "OAuth Token Theft",
        "MFA Fatigue/Bombing",
        "Pass-the-Cookie",
        "Time-based One-Time Password (TOTP) Bypass",
        "Hardware Security Key (FIDO2) Simulated Press",
        "Credential Theft from Password Managers",
        "Kerberos Resource-Based Constrained Delegation (RBCD) Attacks",
        "Shadow Credential Attacks",
      ]
    },
    "F": {
      title: "Hardware & Physical Attacks",
      attacks: [
        "Cold Boot Attack",
        "Evil Maid Attack",
        "Hardware Trojan",
        "Physical Tampering",
        "Side-Channel Attack",
        "Supply Chain Attack",
        "USB Drop Attack",
        "Rowhammer",
        "Firmware Exploitation in IoT/Edge Devices",
        "Spectre/Meltdown Variants",
        "DMA Attacks (Direct Memory Access)",
        "Fault Injection",
        "Power-Supply Glitching",
        "Laser Fault Injection",
        "Acoustic Cryptanalysis",
        "EM Side-Channel (Electromagnetic)",
        "PCI Express (PCIe) DMA Attacks",
      ]
    },
    "G": {
      title: "Cryptographic & Data Attacks",
      attacks: [
        "Birthday Attack",
        "Collision Attack",
        "Data Breach/Exfiltration",
        "Data Diddling",
        "Data Tampering",
        "Downgrade Attack",
        "Known-Plaintext Attack",
        "Man-in-the-Browser (MitB)",
        "Padding Oracle Attack",
        "Harvest Now Decrypt Later",
        "Post-Quantum Cryptography Bypass Prep",
        "Data Poisoning",
        "Freight Train Attacks",
        "Rubber-Hose Cryptanalysis",
        "Homomorphic Encryption Abuse",
        "Length Extension Attack",
        "Oracle Attacks on CBC Mode",
        "Algorithm Confusion Attacks (JWT)",
        "Cryptographic Agility Exploitation",
        "Entropy Depletion in Virtualized Environments",
      ]
    },
    "H": {
      title: "Wireless & IoT Attacks",
      attacks: [
        "Bluejacking",
        "Bluesnarfing",
        "De-authentication Attack",
        "IoT Exploitation",
        "Jamming",
        "KRACK (Key Reinstallation Attack)",
        "NFC Skimming",
        "RFID Cloning/Skimming",
        "BLE Spoofing",
        "Edge Device Compromise",
        "Zigbee/Z-Wave Replay",
        "Matter Protocol Weaknesses",
        "5G Slice Impersonation",
        "LoRaWAN Device Spoofing",
        "Wi-Fi 6/7 Downgrade Attacks",
        "Industrial Wireless (ISA100.11a, WirelessHART) Interference",
        "Satellite IoT (IoT over Satellite) Message Forgery",
        "Automotive ECU Wireless Exploitation",
      ]
    },
    "I": {
      title: "Stealth & Advanced Attacks",
      attacks: [
        "Advanced Persistent Threat (APT)",
        "AI Poisoning/Adversarial AI",
        "Cache Poisoning",
        "Cloud Misconfiguration Exploitation",
        "Abuse Elevation Control",
        "Double-Spending Attack (Cryptocurrency)",
        "Insider Threat",
        "Living-off-the-Land (LOTL)",
        "Malvertising",
        "Memory Scraping",
        "Privilege Escalation",
        "Refactoring Attack",
        "Shadow IT Exploitation",
        "Shimming Attack",
        "Time-of-Check to Time-of-Use (TOCTOU)",
        "Zero-Day Exploit",
        "Prompt Injection",
        "Container Escape",
        "Agentic AI Attacks",
        "Kubernetes/CI-CD Pipeline Attacks",
        "ESXi/Hypervisor Exploitation",
        "Model Evasion/Theft",
        "Tool Misuse in AI Agents",
        "BYOVD (Bring Your Own Vulnerable Driver)",
        "Event Tracing for Windows (ETW) Tampering",
        "DGauss",
        "Process Doppelgänging",
        "Hooking and Splicing",
        "VBA Stomping",
        "DNS Beaconing with Algorithmic Generation",
        "SolarWinds-Style Digital Signature Theft",
        "Trusted Relationship Abuse",
        "Golden Image Poisoning",
        "AI Model Backdooring"
      ]
    }
  };

  const [searchTerm, setSearchTerm] = useState("");

  // Filter attacks based on search term across all categories
  const filteredCategories = Object.entries(attackCategories).reduce((acc, [category, categoryData]) => {
    const filteredAttacks = categoryData.attacks.filter(attack =>
      attack.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filteredAttacks.length > 0 || searchTerm === "") {
      acc[category] = {
        ...categoryData,
        attacks: filteredAttacks
      };
    }

    return acc;
  }, {} as Record<string, { title: string; attacks: string[] }>);

  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Every Type of Cyber Attack: Complete Guide to Threat Categories in 2026",
    description:
      "Complete guide to every type of cyber attack, threat categories, and attack vectors. Learn about malware, phishing, DDoS, APTs, and all major cyber attack classifications in 2026.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/every-type-of-cyber-attack",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="Every Type of Cyber Attack: Complete Guide to Threat Categories"
        description="Complete guide to every type of cyber attack, threat categories, and attack vectors."
        keywords="cyber attacks, types of cyber attacks, cyber security threats, malware types, phishing attacks, DDoS attacks, APT attacks, network attacks, web application attacks, social engineering, ransomware, security vulnerabilities"
        canonical="https://cyberannals.com/blog/every-type-of-cyber-attack"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            Every Type of Cyber Attack
          </h1>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            Complete Guide to Threat Categories in 2026
          </h2>
        </header>

        <div className="space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
          <p className="text-lg">
            This comprehensive guide lists every known type of cyber attack, organized by category. Each attack type is linked to its own detailed page with in-depth information, prevention strategies, and real-world examples.
          </p>

          {/* Search Bar */}
          <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-4">
              <label htmlFor="search" className="font-bold text-slate-700 dark:text-slate-300 min-w-fit">
                Search Attack Types:
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search for specific attack types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="space-y-6 mt-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              Cyber Attack Categories:
            </h2>

            {Object.entries(filteredCategories).map(([category, categoryData]) => (
              <div
                key={category}
                className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex justify-between items-center text-left font-bold text-slate-900 dark:text-white"
                >
                  <span>{category}. {categoryData.title}</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${expandedCategories[category] ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedCategories[category] && (
                  <div className="p-4 bg-white dark:bg-slate-900">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryData.attacks.map((attack, index) => (
                        <Link
                          key={index}
                          to={`/blog/attack/${attack.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
                          className={styles.link + " block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"}
                        >
                          {attack}
                          <span className="ml-2 text-xs text-blue-500">Explore →</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-4 mb-6">
            <Link
              to="/blog"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/decades"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              Explore Cyber Security History →
            </Link>
            <Link
              to="/toolkit"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              Security Tools →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Popular Topics:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/what-is-cybersecurity" className={styles.link}>What is Cybersecurity?</Link></li>
                <li><Link to="/blog/what-is-firewall" className={styles.link}>What is a Firewall?</Link></li>
                <li><Link to="/blog/what-is-vpn" className={styles.link}>What is a VPN?</Link></li>
                <li><Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI Driven Cyber Attacks</Link></li>
                <li><Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Cyber Attack Types:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/common-cyber-attacks" className={styles.link}>Common Cyber Attacks</Link></li>
                <li><Link to="/blog/most-popular-cyber-attacks" className={styles.link}>Most Popular Cyber Attacks</Link></li>
                <li><Link to="/blog/top-10-cyber-attacks" className={styles.link}>Top 10 Cyber Attacks</Link></li>
                <li><Link to="/blog/worst-cyber-attacks" className={styles.link}>Worst Cyber Attacks</Link></li>
                <li><Link to="/blog/every-type-of-cyber-attack" className={styles.link}>Every Type of Cyber Attack</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Related Cyber Attacks:
              </h3>
              <div className="space-y-2">
                <div><Link to="/event/log4j-log4shell-2021" className={styles.link}>Log4j Log4Shell (2021)</Link></div>
                <div><Link to="/event/heartbleed" className={styles.link}>Heartbleed (2014)</Link></div>
                <div><Link to="/event/stuxnet" className={styles.link}>Stuxnet (2010)</Link></div>
                <div><Link to="/event/operation-aurora" className={styles.link}>Operation Aurora (2009)</Link></div>
                <div><Link to="/event/colonial-pipeline-ransomware" className={styles.link}>Colonial Pipeline Attack (2021)</Link></div>
                <div><Link to="/event/equifax-data-breach-2017" className={styles.link}>Equifax Data Breach (2017)</Link></div>
                <div><Link to="/event/bangladesh-bank-heist" className={styles.link}>Bangladesh Bank Heist (2016)</Link></div>
                <div><Link to="/event/anthem-breach" className={styles.link}>Anthem Breach (2015)</Link></div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4">
              <Link to="/sitemap.xml" className={styles.link}>Sitemap</Link>
              <Link to="/privacy-policy" className={styles.link}>Privacy Policy</Link>
              <Link to="/terms-of-service" className={styles.link}>Terms of Service</Link>
              <Link to="/contact" className={styles.link}>Contact Us</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default EveryTypeOfCyberAttack;